<script lang="ts">
    import moment from "moment";

    /**
     * Component used to filter document and operation list
     *
     * usage :
     * <SearchFilterComponent listToFilter={allOperations} bind:filteredList={operations}></SearchFilterComponent>
     * with allOperations the list to filter and operations the list to display.
     *
     * <SearchFilterComponent listToFilter={allDocuments} bind:filteredList={documents} scope="document"></SearchFilterComponent>
     */
    import { onMount } from "svelte";
    import { swClient, user } from "../../stores";

    /** The list to filter */
    export let listToFilter: any[] = [];
    /** The list to diplay */
    export let filteredList: any[] = [];
    /** The scope of the component. Are we on a campaign list or a document list ? campaign by default*/
    export let scope: "campaign" | "document" = "campaign";

    /**The value in the search bar*/
    let searchValue: string;

    /**Options and value for the type of operation*/
    const operationTypeValues: string[] = [
        "Toutes les opérations",
        "Nationale",
        "Régionale",
        "Locale",
        "Mixte",
    ];

    $: {
        // update list when list to filter change
        if (listToFilter) {
            search();
        }
    }
    let operationTypeFilterValue: string = "Toutes les opérations";

    /**Options and value for the centrale names*/
    const centralesValues: string[] = [];
    let centraleFilterValue: string = "all";

    /** Value of the expired checkbox*/
    let expiredFilter: boolean = false;

    /** Date to compare expired value*/
    let now: Date = null;

    /** is the component init ?*/
    let isInit: boolean = false;

    let bottomDateRange: string = moment()
        .subtract(1, "month")
        .format("YYYY-MM-DD");

    let topDateRange: string = moment().add(1, "month").format("YYYY-MM-DD");

    /** debounce timer for search */
    let timer: NodeJS.Timeout = null;

    let userType = null;

    onMount(async () => {
        const geoAreas = await $swClient.get(`/v2/geoareas`);
        for (let i = 0; i < geoAreas.length; i++) {
            const geoArea = geoAreas[i];
            // init centrale Values
            centralesValues.push(geoArea.name);
        }
        now = new Date();
        isInit = true;
        search();
    });

    /** When user press Enter in the search bar, search */
    function handleKeydown(event) {
        if (event.keyCode === 13) {
            search();
        }
    }

    /**
     * search using the given values
     */
    function search() {
        filteredList = [];
        for (let i = 0; i < listToFilter.length; i++) {
            const item = listToFilter[i];
            let expirationOk: boolean = false;
            let searchOk: boolean = false;
            let typeOk: boolean = false;
            let centraleOk: boolean = false;
            let dateRangeOk: boolean = false;
            let searchValueNorm;
            let itemNameNorm;
            let itemCodeNorm;
            if (searchValue) {
                searchValueNorm = searchValue
                    .normalize("NFD")
                    .replace(/[\u0300-\u036f]/g, "");
                itemNameNorm = item.name
                    .normalize("NFD")
                    .replace(/[\u0300-\u036f]/g, "");
                if (item.code) {
                    itemCodeNorm = item.code
                        .normalize("NFD")
                        .replace(/[\u0300-\u036f]/g, "");
                }
            }
            if (
                !bottomDateRange ||
                (new Date(item.startDate) > new Date(bottomDateRange) &&
                    (!topDateRange ||
                        new Date(item.startDate) < new Date(topDateRange)))
            ) {
                dateRangeOk = true;
            }
            if (
                expiredFilter ||
                (!expiredFilter && new Date(item.endDate) > now)
            ) {
                expirationOk = true;
            }
            if (
                !searchValue ||
                (searchValueNorm &&
                    (itemNameNorm
                        ?.toLowerCase()
                        .includes(searchValueNorm.toLowerCase()) ||
                        itemCodeNorm
                            ?.toLowerCase()
                            .includes(searchValueNorm.toLowerCase())))
            ) {
                searchOk = true;
            }
            if (
                operationTypeFilterValue === "Toutes les opérations" ||
                operationTypeFilterValue === item.type
            ) {
                typeOk = true;
            }
            if (
                centraleFilterValue === "all" ||
                centraleFilterValue === item.geo_area?.name
            ) {
                centraleOk = true;
            }
            if (
                expirationOk &&
                searchOk &&
                typeOk &&
                centraleOk &&
                dateRangeOk
            ) {
                filteredList.push(item);
            }
        }
        filteredList = filteredList;
    }

    function searchDebounce() {
        clearTimeout(timer);
        timer = setTimeout(() => {
            search();
        }, 250);
    }
</script>

{#if isInit}
    <div class="columns is-align-items-center">
        <div class="column">
            <input
                class="input is-dark"
                type="text"
                placeholder={scope === "campaign"
                    ? "Nom / Code Opération"
                    : "Code Opération"}
                bind:value={searchValue}
                on:keydown={handleKeydown}
                on:keyup={() => searchDebounce()}
            />
        </div>
        {#if scope === "campaign"}
            <div class="column is-narrow">
                <div class="select is-dark is-fullwidth">
                    <select
                        bind:value={operationTypeFilterValue}
                        on:change={() => search()}
                    >
                        {#each operationTypeValues as typeValue}
                            <option value={typeValue}>{typeValue}</option>
                        {/each}
                    </select>
                </div>
            </div>
        {/if}
        {#if scope === "document"}
            <div class="column is-narrow">
                <div class="select is-dark is-fullwidth">
                    <select
                        bind:value={centraleFilterValue}
                        on:change={() => search()}
                    >
                        <option value="all">TOUS</option>
                        <option value="">NATIONAL</option>
                        {#each centralesValues as centraleValue}
                            <option value={centraleValue}
                                >{centraleValue}</option
                            >
                        {/each}
                    </select>
                </div>
            </div>
        {/if}
        <div class="column is-narrow" style="text-align: center">
            <span>
                Date de début entre
                <input
                    type="date"
                    bind:value={bottomDateRange}
                    max={topDateRange}
                    on:change={() => search()}
                />
                et
                <input
                    type="date"
                    bind:value={topDateRange}
                    min={bottomDateRange}
                    on:change={() => search()}
                />
            </span>
        </div>
        <div class="column is-pulled-right is-narrow">
            <label class="checkbox">
                <input
                    type="checkbox"
                    bind:checked={expiredFilter}
                    on:change={() => search()}
                /> Expirée
            </label>
        </div>
        <!-- <div class="column is-narrow">
            <button class="button is-info" on:click={() => search()}>
                <span class="fa fa-search"></span>
            </button>
        </div> -->
    </div>
{/if}

<style lang="scss">
    @import "../../styles/variables.scss";

    .select {
        border-radius: 10px
    }
</style>
